// Papeis da equipe que passa pelo quarto.
//
// String validada contra catalogo, e nao enum de banco nem texto livre: a lista
// cresce com o caso (chegou fisioterapeuta, chegou fonoaudiologa) e enum pediria
// migration a cada uma; texto livre faria "medico", "Medico" e "med" virarem
// tres papeis que nunca se cruzam. Mesma escolha dos marcadores de laboratorio.

export interface PapelProfissional {
  chave: string;
  rotulo: string;
  /** Como aparece antes do nome na tela: "Enf. Juliana". */
  tratamento: string;
  /** Ordem de exibicao: quem a familia procura primeiro vem antes. */
  ordem: number;
}

export const PAPEIS: PapelProfissional[] = [
  // Quem da familia esta no quarto tambem e plantao — e vem primeiro, porque e
  // quem passa o turno para a proxima pessoa da familia. Sem tratamento: sao
  // chamados pelo nome, e "Sr. Ilton" num papel que o irmao le e estranho.
  { chave: 'familiar', rotulo: 'Familiar / acompanhante', tratamento: '', ordem: 0 },
  // O tratamento do médico é o único que flexiona; ver `comTratamento`.
  { chave: 'medico', rotulo: 'Médico(a)', tratamento: 'Dr(a).', ordem: 1 },
  { chave: 'enfermeiro', rotulo: 'Enfermeiro(a)', tratamento: 'Enf.', ordem: 2 },
  { chave: 'tecnico_enfermagem', rotulo: 'Técnico(a) de enfermagem', tratamento: 'Téc.', ordem: 3 },
  { chave: 'fisioterapeuta', rotulo: 'Fisioterapeuta', tratamento: 'Fisio.', ordem: 4 },
  { chave: 'fonoaudiologo', rotulo: 'Fonoaudiólogo(a)', tratamento: 'Fono.', ordem: 5 },
  { chave: 'nutricionista', rotulo: 'Nutricionista', tratamento: 'Nutri.', ordem: 6 },
  { chave: 'psicologo', rotulo: 'Psicólogo(a)', tratamento: 'Psic.', ordem: 7 },
  { chave: 'assistente_social', rotulo: 'Assistente social', tratamento: 'A. S.', ordem: 8 },
  { chave: 'outro', rotulo: 'Outro', tratamento: '', ordem: 99 },
];

const PORCHAVE = new Map(PAPEIS.map((p) => [p.chave, p]));
export const acharPapel = (chave: string): PapelProfissional | undefined => PORCHAVE.get(chave);

// --------------------------------------------------------------- tratamento
//
// "Dr(a). Pedro" e uma falta de educacao impressa num papel que a familia
// entrega na mao do medico. Todas as outras abreviacoes do catalogo ja sao
// neutras de nascenca ("Enf.", "Fisio.", "Nutri.") — a unica que flexiona e a
// do medico, e por isso e a unica que precisa saber de quem esta falando.
//
// A inferencia acerta a enorme maioria dos nomes brasileiros por terminacao,
// e o resto sai por lista. Quando nao da para saber, volta o "Dr(a)." — sem
// chute: errar o tratamento de uma pessoa e pior do que a forma feia.

const MASCULINOS_EM_A = new Set([
  'luca', 'juca', 'nicola', 'jonatha', 'josua', 'joshua', 'barnaba',
  'zacaria', 'elia', 'aquila', 'atila', 'jeova', 'noa', 'sivuca',
]);

const FEMININOS_SEM_A = new Set([
  'beatriz', 'bia', 'ester', 'esther', 'raquel', 'rachel', 'isabel', 'isabelle',
  'maribel', 'carmen', 'carme', 'iris', 'ines', 'mercedes', 'lourdes', 'solange',
  'eliane', 'adriane', 'josiane', 'simone', 'denise', 'aline', 'caroline',
  'jaqueline', 'jacqueline', 'evelyn', 'rose', 'roseli', 'rute', 'ruth',
  'judite', 'edite', 'nair', 'dulce', 'mabel', 'miriam', 'sarah', 'deborah',
  'hannah', 'noemi', 'emily', 'kelly', 'nancy', 'wendy', 'neide', 'zenaide',
  'marlene', 'irene', 'helen', 'helene', 'karen', 'gleice', 'cleide', 'ivone',
  'zilene', 'lucimare', 'elizabeth', 'elisabete', 'dalve', 'nice', 'alice',
  'clarice', 'berenice', 'eunice', 'janaine', 'luci', 'mari', 'naiane',
]);

const SEM_ACENTO = /[̀-ͯ]/g;
const primeiroNome = (nome: string) =>
  nome.trim().split(/\s+/)[0]!.toLowerCase().normalize('NFD').replace(SEM_ACENTO, '');

/**
 * Feminino, masculino, ou `null` quando nao da para afirmar.
 *
 * `null` e resposta legitima e usada: e o que impede o sistema de chamar de
 * "Dra." um Alex que e homem.
 */
export function generoPeloNome(nome: string): 'f' | 'm' | null {
  const n = primeiroNome(nome);
  if (!n) return null;
  if (FEMININOS_SEM_A.has(n)) return 'f';
  if (MASCULINOS_EM_A.has(n)) return 'm';
  if (n.endsWith('a')) return 'f';
  // Terminacao em -e nao decide nada em portugues: Felipe e Simone rimam.
  if (n.endsWith('e')) return null;
  // "-x" fica de fora: Alex serve para Alexandre e para Alexandra.
  if (/[bcdfglmnprstvzoiu]$/.test(n)) return 'm';
  return null;
}

/** Nome como se escreve: "marilia" virou "Marilia", "DE" continua "de". */
export function arrumarNome(nome: string): string {
  const miudas = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);
  return nome.trim().split(/\s+/).map((palavra, i) => {
    const baixa = palavra.toLowerCase();
    if (i > 0 && miudas.has(baixa)) return baixa;
    // So mexe em palavra toda minuscula: quem digitou "McCarthy" sabe o que quis.
    if (palavra !== baixa) return palavra;
    return baixa.charAt(0).toUpperCase() + baixa.slice(1);
  }).join(' ');
}

/** "Enf. Juliana", "Dr. Pedro". Papel fora do catalogo devolve so o nome. */
export function comTratamento(papel: string, nome: string): string {
  const achado = PORCHAVE.get(papel);
  const arrumado = arrumarNome(nome);
  if (!achado || !achado.tratamento) return arrumado;
  if (papel === 'medico') {
    const genero = generoPeloNome(nome);
    const dr = genero === 'f' ? 'Dra.' : genero === 'm' ? 'Dr.' : 'Dr(a).';
    return `${dr} ${arrumado}`;
  }
  return `${achado.tratamento} ${arrumado}`;
}

/** Medico antes de enfermeira, enfermeira antes de fisioterapeuta. */
export function ordenarPorPapel<T extends { papel: string }>(itens: T[]): T[] {
  return [...itens].sort(
    (a, b) => (PORCHAVE.get(a.papel)?.ordem ?? 98) - (PORCHAVE.get(b.papel)?.ordem ?? 98),
  );
}
